import { useMemo, useRef } from "react";
import ChevronRightIcon from "@heroicons/react/24/solid/ChevronRightIcon";
import { useQuery } from "@tanstack/react-query";
import useResizeObserver from "use-resize-observer";

import { type DataProps } from "@hsl/lgim/src/js/widgets/fundFinder/Widget";

import Spinner from "../../components/Spinner";
import { Translate, TranslationProvider } from "../../hooks/useTranslation";
import SearchInput from "./SearchInput";
import SearchResults from "./SearchResults";
import useFundFinderStore, { FundFinderStoreProvider } from "./store";
import {
    FundFinderConfigJSONSchema,
    type FundFinderConfigJSON,
    type StoreConfig,
} from "./types";

import "../../../tailwind.css";

export type Props = DataProps & {
    configId: string;
};

function FundFinder(_props: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver({ ref: containerRef });
    const isLoading = useFundFinderStore((store) => store.loading);
    const applySearch = useFundFinderStore((store) => store.applySearch);
    const results = useFundFinderStore((store) => store.search.results);
    const absoluteURL = useFundFinderStore((store) => store.getAbsoluteURL);
    const getDataByIdWithType = useFundFinderStore(
        (store) => store.getDataByIdWithType,
    );
    const term = useFundFinderStore((store) => store.search.term);
    const fcURL = useFundFinderStore((store) =>
        store.getExplorerURL("collective"),
    );
    const footer = (
        <div className="flex h-20 items-center justify-end gap-4">
            <span className="@sm:block hidden max-w-[12rem] text-sm">
                <Translate>
                    For advanced search and filter options go to our fund
                    centre.
                </Translate>
            </span>
            <a
                href={fcURL}
                className="bg-core-blue @lg/main:px-5 @lg/main:text-base flex gap-1 px-3 py-3 text-sm font-medium text-white"
            >
                <Translate>Go to fund centre</Translate>
                <ChevronRightIcon width={20} />
            </a>
        </div>
    );

    return isLoading ? (
        <Spinner />
    ) : (
        <div className="@container/main" ref={containerRef}>
            <SearchInput
                onChange={(term) => applySearch(term)}
                onSearch={(term) => applySearch(term)}
                placeholder="Search by fund name, ISIN or ticker"
                placeholderShort="Search funds"
                value={term}
                containerWidth={width}
            />
            {term.length > 0 && (
                <SearchResults
                    results={results}
                    getter={getDataByIdWithType}
                    absoluteURL={absoluteURL}
                    footer={fcURL ? footer : undefined}
                    containerWidth={width}
                />
            )}
        </div>
    );
}

const DEFAULT_BASE_URL = "https://widgets-lgim.huguenots.co.uk";

export default function FundFinderRoot(props: Props) {
    const {
        audience,
        jurisdiction,
        language,
        configId,
        baseurl = DEFAULT_BASE_URL,
        version,
    } = props;
    const { data: configData } = useQuery({
        queryFn: async () =>
            FundFinderConfigJSONSchema.parse(
                await fetchPart<FundFinderConfigJSON>({
                    audience,
                    language,
                    id: configId,
                    baseurl,
                    version,
                }),
            ),
        queryKey: [audience, language, configId, baseurl, version],
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });
    const availableConfigs = useMemo<StoreConfig[]>(() => {
        if (!configData) {
            return [];
        }
        return configData.fundCentres
            .filter(({ exclude, include }) => {
                const restrictions = exclude ?? include;
                if (!restrictions) {
                    return true;
                }
                const { audiences, jurisdictions } = restrictions;
                const hasAudience = audiences
                    ?.map((id) => `${id}`)
                    .includes(audience);
                const hasJurisdiction = jurisdiction
                    ? jurisdictions?.map((id) => `${id}`).includes(jurisdiction)
                    : true;
                const hasEither = hasAudience || hasJurisdiction;
                return exclude ? !hasEither : hasEither;
            })
            .map(({ id, type, url }) => ({
                baseurl,
                audience,
                language,
                id: `${id}`,
                type,
                explorerURL: url,
            }));
    }, [audience, baseurl, configData, jurisdiction, language]);
    return (
        <TranslationProvider value={{}}>
            {availableConfigs.length > 0 ? (
                <FundFinderStoreProvider configs={availableConfigs}>
                    <FundFinder {...props} />
                </FundFinderStoreProvider>
            ) : (
                <Spinner />
            )}
        </TranslationProvider>
    );
}

async function fetchPart<T>({
    audience,
    language,
    version = "live",
    id,
    baseurl,
}: Omit<DataProps, "jurisdiction"> & { id: string }): Promise<T> {
    try {
        const pObj = { audience, language, version, id };
        const params = new URLSearchParams(pObj);
        const res = await fetch(`${baseurl}/srp/api/part?${params.toString()}`);
        return (await res.json()) as T;
    } catch (err) {
        throw new Error(`Error fetching part: ${id}`);
        console.error(err);
    }
}
